import type { PeriodValue } from '../../../components/reports';

/**
 * 🔴 HOW A PICKED PERIOD GOES ON THE WIRE — the one place the attendance report tabs decide it.
 *
 * `PeriodPicker` hands every tab two CALENDAR DAYS (`yyyy-MM-dd`). Each tab used to turn them into
 * `new Date(`${day}T00:00:00`)`, i.e. LOCAL midnight in the READER's browser, and both transports
 * (`object-to-formdata` for the screen, `JSON.stringify` for the export) serialise a `Date` with
 * `toISOString()`. So what reached the server was a UTC INSTANT whose calendar date depends on the
 * browser's zone: east of Greenwich local midnight is the PREVIOUS day in UTC, west of it 23:59:59
 * is the NEXT one. The server then read a calendar day back out of it (`.Date`), or compared it
 * with wall-clock columns, and the report covered the wrong days:
 *
 *   • Overtime    — the whole window one day early for every reader east of UTC (the picker said
 *                   12..13 Oct, the server walked 11..12 Oct and the overtime of the 13th vanished);
 *   • Clockings   — one extra day at the start (east) or at the end (west);
 *   • Leaves      — the whole window one day late for every reader west of UTC;
 *   • WorkSessions, Shifts — the window slid by the reader's UTC offset (hours, not days).
 *
 * The values below are STRINGS WITH NO ZONE DESIGNATOR. Nothing converts them: ASP.NET binds a
 * zone-less value as `DateTimeKind.Unspecified` with the calendar fields exactly as written, on
 * the `[FromForm]` path (`DateTimeModelBinder`) and on the `[FromBody]` path (Newtonsoft) alike. The
 * days the reader picked are therefore the days the server sees, in any browser zone.
 *
 * WHICH ZONE DEFINES "A DAY" is the server's business and differs per report — it is whatever the
 * column the report filters on means:
 *   • Overtime     — the EMPLOYEE's time zone (`User.TimeZone`), resolved per employee server-side;
 *   • Clockings, Shifts, WorkSessions — the employee's own WALL CLOCK at the punch
 *                    (`ClockInTime` / `ClockOutTime` / `StartTime` / `EndTime`), the same reading
 *                    the rows print;
 *   • Leaves       — the calendar dates of the leave request (no zone at all).
 * None of them is the reader's browser.
 *
 * ⚠️ NEVER put a `Date` (or anything ending in `Z` / `+hh:mm`) into `from` / `to` of these reports.
 * On `reports/Shifts` a zoned value is deliberately still honoured as a UTC INSTANT window (a stale
 * bundle of the legacy deep-link export sends one), so it would not even fail — it would quietly go
 * back to depending on the browser. The deep-link downloads in `components/reportsDownload/`
 * (Clockings, Shifts) build their period with the functions below, like the tabs.
 *
 * Out of Bounds is the ONE tab that does not use this file, on purpose: its rows are genuine UTC
 * instants rendered in the reader's zone, so its window is the reader's local days as instants.
 */
export interface ReportPeriodParams {
  from: string;
  to: string;
}

const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;

/** True for a plain `yyyy-MM-dd` calendar day. */
export function isDayKey(value: string | null | undefined): value is string {
  return typeof value === 'string' && DAY_KEY.test(value);
}

/**
 * Both ends are real calendar days. A cleared date input leaves `''` behind; a request built from
 * that must never be sent (the server would refuse it, and an unset `from` on a report that walks
 * day by day is not something to find out about in production).
 */
export function isCompletePeriod(period: PeriodValue): boolean {
  return isDayKey(period.from) && isDayKey(period.to);
}

/**
 * CALENDAR-DAY reports (Overtime, Clockings): the picked day keys, verbatim — `2026-10-12`.
 * The server takes `.Date` of both ends and treats `to` as INCLUSIVE. On the form posts
 * `api/Attendance/reports.ts` sends each as `2026-10-12T00:00:00` (same day, a shape every request
 * culture parses alike); the JSON export bodies carry the bare key.
 */
export function dayKeyPeriod(from: string, to: string): ReportPeriodParams {
  return { from, to };
}

/**
 * WALL-CLOCK reports (Shifts, Leaves, WorkSessions): the first and last second of the picked days
 * as ZONE-LESS readings — `2026-10-12T00:00:00` .. `2026-10-13T23:59:59`. The server compares them
 * as they stand with wall-clock columns (`>= from && <= to`), so `to` has to carry the end of its
 * day; a bare day key there would cut the last day off at midnight.
 */
export function wallClockPeriod(from: string, to: string): ReportPeriodParams {
  return { from: `${from}T00:00:00`, to: `${to}T23:59:59` };
}

/**
 * A `:from` / `:to` segment of the legacy deep-link downloads
 * (`/attendance/reports/<report>/:from/:to/:parameters/:format`, written `MM-dd-yyyy`) -> a day key,
 * or null when it is not a real calendar day.
 *
 * The segment is read TEXTUALLY. The downloads used to build `new Date(year, month - 1, day)` from
 * it and hand that `Date` to the reports API, which serialised it as a UTC instant: the report then
 * covered days that depend on the reader's browser zone (see the top of this file).
 */
export function dayKeyFromLegacyPathDate(raw: string | null | undefined): string | null {
  const match = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec((raw ?? '').trim());
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);
  // Local fields only, to refuse a day the calendar does not have (02-31); no instant is derived.
  const probe = new Date(year, month - 1, day);
  if (probe.getFullYear() !== year || probe.getMonth() !== month - 1 || probe.getDate() !== day) return null;
  return `${match[3]}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * A `from` / `to` query-string value -> a day key, or null when it is not a date.
 *
 * A bare `yyyy-MM-dd` (what the notification links carry) is returned AS WRITTEN. It must not go
 * through `new Date(...)`: the spec parses a date-only string as UTC midnight, which is the previous
 * calendar day for every reader west of Greenwich. Anything else is treated as an instant and read
 * in the reader's zone, as before.
 */
export function dayKeyFromParam(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (isDayKey(trimmed)) return trimmed;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
}
